import React from "react";
import { Container, Typography, Box, List, ListItem, ListItemText } from "@mui/material";

const Section = ({ title, children }) => (
  <Box mb={4}>
    <Typography variant="h5" fontWeight="bold"  align="left" gutterBottom>
      {title}
    </Typography>
    {children}
  </Box>
);

const BulletList = ({ items }) => (
  <List sx={{ listStyleType: "disc", pl: 4 }}>
    {items.map((item, index) => (
      <ListItem key={index} sx={{ display: "list-item", py: 0 }}>
        <ListItemText primary={item} />
      </ListItem>
    ))}
  </List>
);

const PrivacyPolicy = () => {
  return (
    <Container maxWidth="md" 
    sx={{
    py: 6,
    minHeight: "100vh",
    textAlign: "left", 
   // background: "linear-gradient(180deg, rgba(0,123,143,0.08) 100%, rgba(243,146,0,0.08) 0%)",
    borderRadius: 3
  }}>
      
      {/* Page Title */}
      <Typography variant="h3" fontWeight="bold" gutterBottom>
        Privacy Policy
      </Typography>

      {/* Purpose */}
      <Section title="Purpose">
        <Typography paragraph>
          The purpose of this Website Privacy Policy is to explain how OrciMed Life Sciences (OMLS)
          lawfully and responsibly collects, uses, stores, and protects Personal Data obtained
          through its website.
        </Typography>
        <Typography paragraph>
          This policy reflects OMLS’s commitment to compliance with applicable data protection and
          privacy laws, including the General Data Protection Regulation (GDPR) and other relevant
          national and international regulations.
        </Typography>
      </Section>

      {/* Scope */}
      <Section title="Scope">
        <Typography paragraph>
          This Privacy Policy applies to all individuals who access or interact with the OMLS website,
          including visitors who submit inquiries or otherwise provide Personal Data through the website.
        </Typography>
        <Typography paragraph>
          This policy applies solely to website-related data processing and does not cover internal employee
          data processing or client contractual data handled outside the website.
        </Typography>
      </Section>

      {/* Definitions */}
      <Section title="Definitions">
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Applicable Data Protection Laws
        </Typography>
        <Typography paragraph>
          All relevant legal and regulatory requirements governing the processing of Personal Data,
          including GDPR and other applicable national or international privacy laws.
        </Typography>

        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Personal Data
        </Typography>
        <Typography paragraph>
          Any information that directly or indirectly identifies an individual, as defined by applicable
          data protection laws.
        </Typography>

        <Typography>Examples include:</Typography>
        <BulletList
          items={[
            "Name",
            "Email address",
            "Phone number",
            "Professional or business contact details",
            "IP address and online identifiers",
            "Location data",
          ]}
        />

        <Typography variant="h6" fontWeight="bold" mt={3} gutterBottom>
          Processing
        </Typography>
        <Typography paragraph>
          Any operation performed on Personal Data, whether automated or manual, including collection,
          recording, organization, storage, retrieval, use, disclosure, restriction, erasure, or destruction.
        </Typography>

        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Data Protection Officer (DPO)
        </Typography>
        <Typography paragraph>
          An individual appointed by OrciMed Life Sciences (OMLS) responsible for overseeing compliance
          with data protection requirements.
        </Typography>
      </Section>

      {/* Personal Data Collected */}
      <Section title="Personal Data Collected">
        <Typography variant="h6" fontWeight="bold">
          Data Provided by You
        </Typography>

        <Typography>OMLS may collect:</Typography>
        <BulletList
          items={[
            "Name",
            "Email address",
            "Phone number",
            "Company or organization name",
            "Information submitted in website forms",
          ]}
        />

        <Typography variant="h6" fontWeight="bold" mt={2}>
          Automatically Collected Data
        </Typography>

        <BulletList
          items={[
            "IP address",
            "Browser type and version",
            "Device and operating system",
            "Pages visited and time spent",
          ]}
        />
      </Section>

      {/* Purpose & Legal Basis */}
      <Section title="Purpose and Legal Basis for Processing">
        <Typography>Personal Data is processed for:</Typography>
        <BulletList
          items={[
            "Responding to inquiries",
            "Security and misuse prevention",
            "Legal and regulatory compliance",
          ]}
        />
      </Section>

      {/* Cookies */}
      <Section title="Cookies and Similar Technologies">
        <Typography paragraph>
          OMLS may use cookies to support functionality, analyze usage, and improve user experience.
        </Typography>
        <Typography paragraph>
          You may disable cookies through browser settings. Some features may be affected.
        </Typography>
      </Section>

      {/* Disclosure */}
      <Section title="Disclosure of Personal Data">
        <Typography paragraph>OMLS does not sell or rent Personal Data.</Typography>

        <Typography>Data may be shared:</Typography>
        <BulletList
          items={[
            "With trusted service providers",
            "When required by law",
            "To protect rights, property, or security",
          ]}
        />
      </Section>

      {/* Security */}
      <Section title="Data Security">
        <Typography paragraph>
          OMLS implements technical and organizational safeguards to protect Personal Data.
        </Typography>
      </Section>

      {/* Rights */}
      <Section title="Individual Rights">
        <Typography>You may have the right to:</Typography>
        <BulletList
          items={[
            "Access Personal Data",
            "Request correction or deletion",
            "Restrict or object to processing",
            "Withdraw consent",
            "Lodge complaints with authorities",
          ]}
        />
      </Section>

      {/* Changes */}
      <Section title="Changes to This Policy">
        <Typography paragraph>
          OMLS may update this policy periodically. Updates will be posted on this page.
        </Typography>
      </Section>

      {/* Contact */}
      <Section title="Contact Information">
        <Typography paragraph>
          <strong>Data Protection Officer</strong>
          <br />
          OrciMed Life Sciences (OMLS)
          <br />
          Email: dataprotection@orcimedlifesciences.com
        </Typography>
      </Section>

    </Container>
  );
};

export default PrivacyPolicy;
